/**
 * The accumulating debate context: what one speaker can see, in order.
 *
 * WHY THIS EXISTS. The rebuttal loop used to hand the model a fixed six-message
 * window: the intro, the staging note, the question, whatever this speaker last
 * said, whatever the opponent last said, and the live note. Six messages on
 * exchange one, six on exchange four. Nothing accumulated.
 *
 * That has two consequences. An agent cannot see the arc of its own argument, so
 * it repeats itself and cannot notice that a point has already been answered.
 * And there is nothing for a context budget to work on, because the context does
 * not grow. Compression without accumulation is a mechanism that never fires.
 *
 * So the transcript accumulates here, and `contextBudget` decides what to spend
 * when it gets long.
 *
 * THE SHAPE OF A TURN. Every assistant turn in this protocol was generated as
 * one continuous piece: a private note, an unfinished hand-off, then the speech.
 * That is what assistant prefill produces. So the history stores the note and the
 * speech as SEPARATE entries and rejoins them at the last moment. Separate,
 * because the note is compressible and the speech is not, and a budget cannot
 * make that distinction inside a single string. Rejoined, because a chat template
 * cannot represent two assistant turns in a row and because rejoining reproduces
 * exactly the text the model generated.
 */

import type { BudgetedMsg, Role } from "./contextBudget.ts";

/**
 * The unfinished sentence a prefilled turn ends on.
 *
 * Deliberately mid-thought. A prefill that ends on a full stop invites the model
 * to treat it as a completed remark and respond to it, which is exactly what
 * happened: opening statements began "That's a solid starting point". An
 * unfinished clause can only be continued.
 *
 * It lives here rather than in `debateRounds` because both the message assembly
 * and the flattener need it, and the flattener is the lower layer.
 */
export const HANDOFF = "So, to answer the question directly:";

/**
 * The word an agent says when it has finished.
 *
 * Bare, not parenthesised, and not a sentence. Era-2 measured a real penalty for
 * decorated sentinels, and a small model asked "would you like to respond?" in
 * prose answers in prose, which cannot be parsed. One token it can emit is the
 * only reliable option.
 */
export const REST_TOKEN = "rest";

/** One thing that has already happened on this question. */
export type Beat =
  /** This speaker's own turn: the note it wrote, then what it actually said. */
  | { who: "self"; note?: string; text: string }
  /** The opponent's turn. Only ever their words, never their note. */
  | { who: "other"; text: string };

export type SpeakerView = {
  intro: string;
  stagingNote: string;
  question: string;
  /** Everything already said on this question, oldest first. */
  beats?: Beat[];
  /** What to do NOW. Attached to the last incoming turn, not to the top. */
  instruction: string;
  /**
   * The memory banner for THIS turn. Attached to the last incoming turn as
   * well, because the retrieved facts are about the thing being answered now
   * and belong next to it rather than in a header nobody is reading by then.
   */
  facts?: string;
  /** The note just written, prefilled so the answer continues it. */
  liveNote?: string;
};

/**
 * Build one speaker's labelled context.
 *
 * Labels are the whole point: `intro` is pinned, `spoken` is never summarised,
 * `note` is the only compressible kind. The budget reads nothing but these.
 */
export function buildContext(view: SpeakerView): BudgetedMsg[] {
  const { intro, stagingNote, question, beats = [], instruction, facts, liveNote } = view;

  const out: BudgetedMsg[] = [
    { role: "user", content: intro, kind: "intro" },
    // An empty assistant message breaks some chat templates, so there is always
    // something here even when staging produced nothing.
    { role: "assistant", content: stagingNote || "I am ready.", kind: "note" },
    { role: "user", content: `The question is: "${question}"`, kind: "spoken" },
  ];

  for (const b of beats) {
    if (b.who === "self") {
      if (b.note) out.push({ role: "assistant", content: b.note, kind: "note" });
      // A speaker who has not said anything yet contributes no turn. Pushing an
      // empty one would leave a blank assistant message, which some chat
      // templates reject and none render usefully.
      if (b.text) out.push({ role: "assistant", content: b.text, kind: "spoken" });
    } else if (b.text) {
      out.push({ role: "user", content: b.text, kind: "spoken" });
    }
  }

  // The instruction and the facts ride on the LAST incoming turn.
  //
  // Historical opponent turns must not carry a live instruction. Left on an old
  // beat it reads as a standing order to reply to something already answered,
  // which is one more way for an agent to restate itself.
  //
  // Separated by blank lines, deliberately. The previous assembly built this
  // with `.filter(Boolean).join("\n")`, which silently removed the blank
  // separators along with the absent facts, so the opponent's quoted words ran
  // straight into the instruction with no gap. Where their words stop and the
  // orders start is exactly the boundary the rebuttal round keeps losing.
  const lastUser = lastIndexOfRole(out, "user");
  if (lastUser >= 0) {
    out[lastUser] = {
      ...out[lastUser],
      content: `${out[lastUser].content}\n\n${instruction}`,
    };
    // The memory banner goes in as its OWN message immediately before that
    // turn, not prepended to it. Adjacent and same-role, so the flattener
    // rejoins them and the model sees exactly what it saw before.
    //
    // Separate because it must be separately DISPOSABLE. It is the only part of
    // the context that was neither spoken nor thought: it is retrieved fresh
    // every turn and costs one search to get back. Prepended, it was welded to a
    // message the budget refuses to drop, and one side of a measured debate sat
    // 14% over its window with no lever left to pull.
    if (facts) out.splice(lastUser, 0, { role: "user", content: facts, kind: "facts" });
  }

  if (liveNote) out.push({ role: "assistant", content: liveNote, kind: "note" });
  return out;
}

function lastIndexOfRole(msgs: BudgetedMsg[], role: Role): number {
  for (let i = msgs.length - 1; i >= 0; i--) if (msgs[i].role === role) return i;
  return -1;
}

/**
 * One labelled piece of a message, kept through the flatten.
 *
 * WHY, and it is Free's framing: "any chunk that was input to the model should
 * be visibly 1 chunk. I just want essentially all chunks along the way
 * identifiable."
 *
 * Both halves of that matter. The memory banner, the opponent's turn and the
 * instruction ARE one message by the time the model sees them, because most
 * chat templates cannot represent two consecutive user turns, and drawing them
 * as three boxes would show an interior more organised than the one that
 * exists. But they came from three places, and losing that is what made the
 * biggest block in every card unreadable.
 *
 * So: one message, parts recorded. The display can be honest about both.
 */
export type MsgPart = { kind: BudgetedMsg["kind"]; content: string };

export type Msg = { role: Role; content: string; parts?: MsgPart[] };

/**
 * Collapse a labelled context into something a chat template will accept.
 *
 * Two jobs. Adjacent same-role messages merge, because no chat template can
 * represent two assistant turns in a row and several reject the attempt outright.
 * And a note followed by that same speaker's speech is rejoined THROUGH the
 * hand-off, which reconstructs the exact continuous turn the model generated:
 * note, unfinished clause, speech.
 *
 * A trailing note gets the hand-off with nothing after it. That is the prefill,
 * and leaving the sentence unfinished is what stops the model answering its own
 * thought instead of continuing it.
 */
export function flattenForModel(msgs: BudgetedMsg[]): Msg[] {
  const out: {
    role: Role;
    content: string;
    kind: BudgetedMsg["kind"];
    parts: MsgPart[];
  }[] = [];
  for (const m of msgs) {
    const prev = out[out.length - 1];
    if (prev && prev.role === m.role) {
      const thinking = prev.kind === "note" || prev.kind === "gist";
      // Computed once and used for both the joined content and the part, so the
      // parts always concatenate back to exactly `content`. A part list that
      // does not reconstruct the message would be a display that lies.
      const piece =
        thinking && m.kind === "spoken" && m.role === "assistant"
          ? `${HANDOFF} ${m.content}`
          : m.content;
      prev.content = `${prev.content}\n\n${piece}`;
      prev.parts.push({ kind: m.kind, content: piece });
      prev.kind = m.kind;
      continue;
    }
    out.push({
      role: m.role,
      content: m.content,
      kind: m.kind,
      parts: [{ kind: m.kind, content: m.content }],
    });
  }

  const last = out[out.length - 1];
  if (last && last.role === "assistant" && (last.kind === "note" || last.kind === "gist")) {
    last.content = `${last.content}\n\n${HANDOFF}`;
    // The hand-off belongs to the part it was appended to, or the parts stop
    // reconstructing the message.
    const tail = last.parts[last.parts.length - 1];
    if (tail) tail.content = `${tail.content}\n\n${HANDOFF}`;
  }
  return out.map(({ role, content, parts }) => ({ role, content, parts }));
}

/** The prefill a flattened context ends on, so it can be stripped from the reply. */
export function trailingPrefill(msgs: BudgetedMsg[]): string {
  const flat = flattenForModel(msgs);
  const last = flat[flat.length - 1];
  return last && last.role === "assistant" ? last.content : "";
}
